import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useCookies } from 'react-cookie';
import { ServiceRequests } from 'components/ServiceRequests';
import { ServiceAdvisorDetails } from 'components/ServiceAdvisorDetails';
import { VideoPlayer } from 'components/videoPlayer';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { successToast } from 'components/toasts/success';
import { ContactInfoModal } from 'components/modals/ContactInfoModal';
import { MediaGallery } from 'components/MediaGallery';
import { ItemDetails } from 'components/ItemDetails';
import { SchedulingDrive } from 'components/scheduling/SchedulingDrive';
import { ScheduledDrive } from 'components/scheduling/ScheduledDrive';
import { SchedulingDriveCalendly } from 'components/scheduling/SchedulingDriveCalendly';
import { CtaButtons } from 'components/CtaButtons';
import ReactionAndComment from 'components/reactionAndComments';
import { PlayerReactions } from 'components/reactionAndComments/PlayerReactions';
import { CustomLandingPage } from 'components/customLandingPage/CustomLandingPage';
import { ProtectedVideo } from 'components/ProtectedVideo';
import { Conversations } from 'components/conversations';
import { ModalVideoCTA } from 'components/modals/ModalVideoCTA';
import { Map } from 'components/Map';
// context
import { useReaction } from 'context/reactions/ReactionProvider';
//queries
import { useVideoSecurityAuthMutation } from 'queries/security/videoSecurityAuthMutation';
import { usePlaybackDetailsQuery } from 'queries/details/detailsQuery';
//types
import { QuoteItem } from 'queries/approveOrder/types';
import { AnnotationItem } from 'queries/details/types';
import {
  InteractiveCTAOption,
  PlaybackData,
  INTERNAL_CUSTOMER,
  CALENDAR_VIEW,
  CALENDAR_PLATFORM,
} from 'types';
import { useApproveQuoteItemsMutation } from 'queries/approveOrder/approveQuoteItemsMutation';
import { Backdrop } from 'components/modals/style';
import { IoMdClose, IoMdOpen } from 'react-icons/io';
import {
  AdditionalTextContainer,
  Column,
  Container,
  IFrameContainer,
  InnerContainer,
  MinimizedVideoContainer,
  OverlayIcon,
  OverlayIconWrapper,
  ReactionContainer,
  VideoCtaWrapper,
} from 'style/Main';
import { getOverlayData } from 'utils/helpers';
import styled from 'styled-components/macro';
import NotAvailable from 'components/NotAvailable';
import { useTranslation } from 'react-i18next';

const Header = styled.div`
  width: 100%;
  height: 64px;
  box-sizing: border-box;
  display: flex;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.12);
  justify-content: center;
  align-items: center;
  padding: 7px 20px;
`;

export const Main = () => {
  const {
    reactionsQuery: { refetch: fetchReactions },
  } = useReaction();

  const { t } = useTranslation();
  const [playbackData, setPlaybackData] = useState<PlaybackData>();
  const [isLoading, setIsLoading] = useState(true);
  const [showContactInfoModal, setShowContactInfoModal] = useState(false);
  const [interactiveCTAOptions, setInteractiveCTAOptions] = useState<
    InteractiveCTAOption[]
  >([]); // interactiveCTAs, setVideoPlayerContactButtons
  const [viewerStatisticId, setViewerStatisticId] = useState(0);
  const [showCtaVideoModal, setShowCtaVideoModal] = useState('');

  const [view, setView] = useState(CALENDAR_VIEW.LANDING);
  const [areControlsShown, setAreControlsShown] = useState(true);
  const [isProtected, setIsProtected] = useState(true);
  const [passwordVerified, setPasswordVerified] = useState(false);
  //
  const [isPlayerOpen, setIsPlayerOpen] = useState(true);
  const [isPlayerMinimized, setIsPlayerMinimized] = useState(false);
  const [quoteItemUpdated, setQuoteItemUpdated] = useState(false);

  const [cookies] = useCookies(['covideo-preview-data']);
  const search = useLocation().search;
  const preview = new URLSearchParams(search).get('preview');
  const notifiyFlag = new URLSearchParams(search).get('nn');
  const isPreview = preview !== null;
  const triggerViewNotification = !isPreview && notifiyFlag !== '1';
  const [annotations, setAnnotations] = useState<AnnotationItem[]>([]);

  const videoRef = useRef<HTMLVideoElement>();

  const { data, isSuccess, isError } = usePlaybackDetailsQuery();

  let { shortUrl } = useParams();

  const {
    mutateAsync: videoSecurityAuthMutation,
    isLoading: isLoadingVideoSecurity,
  } = useVideoSecurityAuthMutation({ setPasswordVerified });
  const preparePlaybackData = async (playbackDataResponse: PlaybackData) => {
    if (!playbackDataResponse || !playbackDataResponse.video) {
      return;
    }
    if (triggerViewNotification) {
      setViewerStatisticId(playbackDataResponse?.viewerStatisticId);
    }

    let tempPlaybackData = { ...playbackDataResponse };

    if (isPreview) {
      const cookieRepairOrder = cookies['covideo-preview-data']?.repairOrder;
      tempPlaybackData.repairOrder = {
        ...tempPlaybackData?.repairOrder,
        ...cookieRepairOrder,
      };
    }

    setPlaybackData(tempPlaybackData);
    setIsProtected(!!playbackDataResponse?.video.protected);
    setPasswordVerified(!playbackDataResponse?.video.protected);

    setIsLoading(false);
  };

  const handleQuoteItemClick = (updatedQuoteItems: Array<QuoteItem>) => {
    const repairOrder = playbackData?.repairOrder;
    if (!repairOrder) {
      return;
    }
    repairOrder.quoteItems = [...updatedQuoteItems];
    playbackData.repairOrder = { ...repairOrder };
    setPlaybackData(playbackData);
    setQuoteItemUpdated(true);
  };

  const { mutateAsync: approveQuoteItemsMutation } =
    useApproveQuoteItemsMutation();

  const approveQuoteItems = async () => {
    const repairOrder = playbackData?.repairOrder;
    if (!quoteItemUpdated || !playbackData || !repairOrder) {
      return;
    }

    const approvedRepairOrder = approveQuoteItemsMutation({
      data: { quoteItems: repairOrder.quoteItems || [] },
    });

    setPlaybackData({
      ...playbackData,
      repairOrder: { ...playbackData.repairOrder, ...approvedRepairOrder },
    });

    setQuoteItemUpdated(false);

    successToast({
      title: t('Quote successfully updated!'),
    });
  };

  useEffect(() => {
    if (isSuccess) {
      preparePlaybackData(data);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setIsLoading(false);
    }
  }, [isError]);

  useEffect(() => {
    if (!playbackData) {
      return;
    }

    // generate and set advisor contact video player cta buttons
    const advisorData = playbackData.advisor;
    const interactiveCTAa = playbackData.interactiveCTAs;
    if (advisorData) {
      let actions: InteractiveCTAOption[] = [];
      if (interactiveCTAa) {
        interactiveCTAa.forEach(cta => {
          if (cta.isVisible) {
            actions.push({
              label: cta.title,
              value: cta.url,
              type: cta.type,
              linkValue: cta.linkValue,
            });
          }
        });
      }

      // TODO temporarily hide start conversation button, check this when enabling conversations
      // if (isInternalCustomer(playbackData) && !conversationId) {
      //   actions.push({
      //     label: 'Start conversation',
      //     value: CTAButtonsValues.START_CONVERSATION,
      //     type: CTAButtonsValues.CONVERSATION,
      //     linkValue: '',
      //   });
      // }

      setInteractiveCTAOptions(actions);
    }

    // generate and set annotations
    const annotationArray = [];
    if (playbackData?.annotations && playbackData?.annotations.length) {
      annotationArray.push(...playbackData.annotations);
    }
    if (
      playbackData?.companyAnnotations &&
      playbackData?.companyAnnotations.length
    ) {
      annotationArray.push(...playbackData.companyAnnotations);
    }
    setAnnotations(annotationArray);
  }, [playbackData]);

  // double check if this is needed
  useEffect(() => {
    if (!playbackData?.reactionsEnabled) {
      return;
    }
    const visitorCode = localStorage?.getItem('visitorCode');
    if (!visitorCode) {
      const randomCode = Math.random().toString(36).substr(2, 6);
      localStorage.setItem('visitorCode', randomCode);
    }
    fetchReactions();
  }, [playbackData]);

  useEffect(() => {
    return () => {
      if (videoRef && videoRef.current) {
        videoRef.current.onloadedmetadata = null;
      }
    };
  }, [videoRef.current]);

  const openShowContactInfoModal = () => {
    setShowContactInfoModal(true);
  };

  const renderAutomotiveItems = () => {
    return (
      <Column>
        {!!playbackData?.repairOrder?.quoteItems &&
          !!playbackData.repairOrder.quoteItems.length && (
            <ServiceRequests
              isPreview={isPreview}
              onSubmit={approveQuoteItems}
              handleQuoteItemClick={handleQuoteItemClick}
              quoteItems={playbackData.repairOrder.quoteItems}
              isApproved={playbackData.repairOrder.status === 'Responded'}
              mainColor={playbackData.playerDesign?.playerBackgroundColor}
              quoteItemUpdated={quoteItemUpdated}
            />
          )}
        {playbackData?.inventoryItem && (
          <ItemDetails
            item={playbackData.inventoryItem}
            isPreview={isPreview}
            userCalendarPlatform={playbackData.calendar?.platform}
            calendarWebsiteUrl={playbackData.calendar?.calendarWebsiteUrl}
            dealerSocketFormLink={playbackData?.dealerSocketFormLink}
            onChangeView={setView}
            mainColor={playbackData.playerDesign?.playerBackgroundColor}
            mainTextColor={playbackData.playerDesign?.playerIconsAndTextColor}
          />
        )}
        {playbackData?.isAutomotive && (
          <ServiceAdvisorDetails
            advisorDetails={playbackData.advisor}
            isSales={playbackData.isAutomotiveSales}
          />
        )}
        {playbackData?.mapIframeEmbedCode && (
          <Map mapIframeEmbedCode={playbackData.mapIframeEmbedCode} />
        )}
      </Column>
    );
  };

  const renderMediaQuery = (designOverride?: any) => {
    return (
      playbackData?.inventoryItem && (
        <VideoCtaWrapper>
          <MediaGallery
            orderDetails={playbackData}
            interactiveCTAs={interactiveCTAOptions}
            userId={viewerStatisticId}
            media={playbackData.inventoryItem.media}
            isPreview={isPreview}
            design={designOverride || playbackData.playerDesign}
            videoRef={videoRef}
            showContactInfoModal={showContactInfoModal}
            openShowContactInfoModal={openShowContactInfoModal}
            annotations={annotations}
            areControlsShown={areControlsShown}
            playbackData={playbackData}
          />
          {!playbackData.landingPage?.pageData && (
            <>
              {renderCtaSet('800px')}
              <AdditionalTextContainer
                className='additional-text'
                additionalTextColor={
                  playbackData.automotiveLanding?.additionalTextColor || ''
                }
              >
                {renderAdditionalText()}
              </AdditionalTextContainer>
            </>
          )}
        </VideoCtaWrapper>
      )
    );
  };

  const renderCtaSet = (width?: string) => {
    return (
      playbackData?.ctaSet?.id && (
        <CtaButtons
          ctaSetData={playbackData.ctaSet}
          color={playbackData.automotiveLanding?.bgColor || ''}
          width={width || 'auto'}
          viewerStatisticId={viewerStatisticId}
          videoId={playbackData.videoId}
          shortUrl={shortUrl || ''}
          playbackData={playbackData}
        />
      )
    );
  };

  const renderAdditionalText = () => {
    return parse(playbackData?.automotiveLanding?.additionalText || '');
  };

  // render text from sales page
  const renderFooterAdditionalText = () => {
    return parse(playbackData?.automotiveLanding?.footerAdditionalText || '');
  };

  const renderPlayerReactions = () => {
    return (
      <PlayerReactions
        hasControls={areControlsShown}
        videoDetails={playbackData?.video}
      />
    );
  };

  const unlockVideo = async (password?: string) => {
    if (!playbackData?.videoId) {
      return;
    }
    await videoSecurityAuthMutation({
      shortUrl,
      password,
    });
  };

  const renderVideoPlayer = (
    playbackData: PlaybackData,
    style?: CSSProperties
  ) => {
    return (
      <VideoPlayer
        width={style?.width ? `${style.width}px` : '620px'}
        height={style?.height ? `${style.height}px` : '320px'}
        preventWideSize={true}
        videoSource={playbackData.video?.videoSource}
        thumbnail={playbackData.video?.thumbnail}
        videoRef={videoRef}
        interactiveCTAs={interactiveCTAOptions}
        videoId={playbackData.videoId}
        userId={viewerStatisticId}
        isPreview={isPreview}
        playerBackgroundColor={playbackData.playerDesign?.playerBackgroundColor}
        playerIconsColor={playbackData.playerDesign?.playerIconsAndTextColor}
        playButtonPosition={playbackData.playerDesign?.playButtonPosition}
        isAutomotive={true}
        previewAnnotation={annotations}
        setAreControlsShown={setAreControlsShown}
        videoChapters={playbackData.video?.chapters || []}
        showContactInfoModal={showContactInfoModal}
        openShowContactInfoModal={openShowContactInfoModal}
        hidePoweredBy={playbackData.hidePoweredBy}
        transcript={playbackData.transcript}
      />
    );
  };

  if (isLoading || isLoadingVideoSecurity) {
    return (
      <LoadingIndicator
        isLoading={isLoading}
        height={'100vh'}
        text={t('Loading...')}
      />
    );
  }

  if (!playbackData) {
    return <NotAvailable />;
  }

  if (playbackData.useWebsiteOverlay) {
    const overlayUrl =
      playbackData?.websiteUrl?.url || `https://www.nonexistent/`;
    const { url, sandbox } = getOverlayData(overlayUrl);

    const renderOverlayVideoComponent = () => (
      <VideoCtaWrapper>
        <OverlayIconWrapper isMinimized={isPlayerMinimized}>
          <OverlayIcon onClick={() => setIsPlayerMinimized(!isPlayerMinimized)}>
            <IoMdOpen
              color='white'
              size='30px'
              style={{
                transform: isPlayerMinimized
                  ? 'rotate(-90deg)'
                  : 'rotate(90deg)',
              }}
            />
          </OverlayIcon>
          <OverlayIcon onClick={() => setIsPlayerOpen(false)}>
            <IoMdClose color='white' size='30px' />
          </OverlayIcon>
        </OverlayIconWrapper>
        {isPlayerMinimized ? (
          <MinimizedVideoContainer>
            {renderVideoPlayer(playbackData, {
              width: 420,
              height: 260,
            })}
          </MinimizedVideoContainer>
        ) : (
          renderVideoPlayer(playbackData)
        )}
      </VideoCtaWrapper>
    );

    const renderOverlayPlayer = () => {
      return (
        isPlayerOpen &&
        (isPlayerMinimized ? (
          renderOverlayVideoComponent()
        ) : (
          <Backdrop>{renderOverlayVideoComponent()}</Backdrop>
        ))
      );
    };

    return (
      <IFrameContainer>
        <iframe
          sandbox={sandbox}
          src={url}
          title='website-overlay'
          name='website-overlay'
          allowFullScreen
          height='100%'
          width='100%'
          style={{ borderWidth: '0px', display: 'block' }}
        ></iframe>
        {renderOverlayPlayer()}
      </IFrameContainer>
    );
  }

  const getSchedulingViews = () => {
    return (
      <>
        {view === CALENDAR_VIEW.SCHEDULING &&
          playbackData.calendar &&
          (playbackData.calendar.platform === CALENDAR_PLATFORM.GOOGLE ||
            playbackData.calendar.platform === CALENDAR_PLATFORM.OUTLOOK) && (
            <Container
              bgColor={playbackData.automotiveLanding?.bgColor || ''}
              headerHeight='64px'
              paddingOnMobile='0'
            >
              <SchedulingDrive
                orderDetails={playbackData}
                onChangeView={setView}
                setOrderDetails={setPlaybackData}
                inventoryItem={playbackData.inventoryItem}
                mainColor={playbackData.playerDesign?.playerBackgroundColor}
                mainTextColor={
                  playbackData.playerDesign?.playerIconsAndTextColor
                }
              />
            </Container>
          )}
        {view === CALENDAR_VIEW.SCHEDULING &&
          playbackData.calendar &&
          playbackData.calendar.platform === CALENDAR_PLATFORM.CALENLDY && (
            <Container
              bgColor={playbackData.automotiveLanding?.bgColor || ''}
              headerHeight='64px'
              paddingOnMobile='0'
            >
              <SchedulingDriveCalendly
                orderDetails={playbackData}
                onChangeView={setView}
                inventoryItem={playbackData.inventoryItem}
              />
            </Container>
          )}
        {view === CALENDAR_VIEW.SCHEDULED && (
          <Container
            bgColor={playbackData.automotiveLanding?.bgColor || ''}
            headerHeight='64px'
            paddingOnMobile='0'
          >
            <ScheduledDrive
              orderDetails={playbackData}
              item={playbackData.inventoryItem}
            />
          </Container>
        )}
      </>
    );
  };

  if (isProtected && !passwordVerified) {
    return <ProtectedVideo loading={false} onSubmit={unlockVideo} />;
  }

  const showCustomLandingPage = !!playbackData?.landingPage?.pageData;

  const conversationUsers = [
    '179960',
    '180391',
    '161228',
    '161229',
    '162084',
    '150871',
    '163707',
    '41571',
    '146604',
    '144294',
    '73948',
    '46870',
    '116758',
    '59777',
  ];

  return (
    <>
      {showCustomLandingPage && (
        <>
          {view === CALENDAR_VIEW.LANDING && (
            <CustomLandingPage
              videoRef={videoRef}
              pageData={playbackData.landingPage?.pageData}
              ctaSetData={playbackData.ctaSet}
              renderPlayerReactions={renderPlayerReactions}
              reactionsAndCommentsEnabled={
                playbackData.reactionsEnabled || playbackData.erid
              }
              renderAdditionalText={renderAdditionalText}
              renderAutomotiveItems={renderAutomotiveItems}
              orderDetails={playbackData}
              interactiveCTAs={interactiveCTAOptions}
              design={playbackData.playerDesign}
              viewerStatisticId={viewerStatisticId}
              renderMediaQuery={renderMediaQuery}
              inventoryItem={playbackData.inventoryItem}
              shortUrl={shortUrl || ''}
              transcript={playbackData.transcript}
            />
          )}
          {getSchedulingViews()}
        </>
      )}

      {!showCustomLandingPage && (
        <>
          <Header>
            {playbackData.automotiveLanding?.imageUrl && (
              <img
                src={playbackData.automotiveLanding?.imageUrl}
                alt='logo'
                style={{ maxHeight: '100%', maxWidth: '100%' }}
                crossOrigin='anonymous'
              />
            )}
          </Header>
          {view === CALENDAR_VIEW.LANDING && (
            <Container
              bgColor={playbackData.automotiveLanding?.bgColor || ''}
              headerHeight='64px'
              display='static'
            >
              <InnerContainer>
                {renderMediaQuery()}
                {!playbackData.inventoryItem && (
                  <VideoCtaWrapper>
                    {renderVideoPlayer(playbackData)}
                    <ReactionContainer>
                      {renderPlayerReactions()}
                    </ReactionContainer>
                    {(playbackData.reactionsEnabled || playbackData.erid) && (
                      <>
                        <ReactionAndComment
                          videoRef={videoRef}
                          playbackData={playbackData}
                        />
                        {renderCtaSet()}
                      </>
                    )}
                    <AdditionalTextContainer
                      className='additional-text'
                      additionalTextColor={
                        playbackData.automotiveLanding?.additionalTextColor ||
                        ''
                      }
                      additionalTextBackgroundColor={
                        playbackData.automotiveLanding
                          .additionalTextBackgroundColor
                      }
                    >
                      {renderAdditionalText()}
                    </AdditionalTextContainer>
                  </VideoCtaWrapper>
                )}
                {renderAutomotiveItems()}

                <AdditionalTextContainer
                  className='additional-text'
                  additionalTextColor={
                    playbackData.automotiveLanding?.additionalTextColor || ''
                  }
                >
                  {renderFooterAdditionalText()}
                </AdditionalTextContainer>
              </InnerContainer>
            </Container>
          )}
          {getSchedulingViews()}
        </>
      )}
      {showContactInfoModal && (
        <ContactInfoModal
          handleCloseModal={() => setShowContactInfoModal(false)}
          interactiveCTAs={interactiveCTAOptions}
          setShowCtaVideoModal={setShowCtaVideoModal}
        />
      )}
      {(playbackData.customerId.toString() === INTERNAL_CUSTOMER.DEV ||
        conversationUsers.includes(playbackData.userId.toString())) && (
        <Conversations
          advisorDetails={playbackData.advisor}
          receiver={playbackData.receiver}
        />
      )}
      {showCtaVideoModal !== '' && (
        <ModalVideoCTA
          handleCloseModal={() => setShowCtaVideoModal('')}
          videoSource={showCtaVideoModal}
        />
      )}
    </>
  );
};
